import { useCallback, useEffect, useState } from 'react';
import type {
  CreateOrderInput,
  OrderDto,
  ProductCategoryDto,
  ProductListResponse,
  TagDto,
} from '@gc/shared';

import { api, ApiError } from '@/lib/api';
import { uploadRaster } from '@/features/tabs/rasterUpload';
import { stationTicketToRaster } from '@/features/tabs/receiptCanvas';

export interface ProductFilters {
  search: string;
  productCategoryId: string | null;
  tagId: string | null;
  /** Menu mode hides de-listed products; the settings table shows them. */
  onlyActive: boolean;
  lowStockOnly: boolean;
}

export const EMPTY_PRODUCT_FILTERS: ProductFilters = {
  search: '',
  productCategoryId: null,
  tagId: null,
  onlyActive: false,
  lowStockOnly: false,
};

function toQueryString(filters: ProductFilters): string {
  const params = new URLSearchParams();
  if (filters.search.trim()) params.set('search', filters.search.trim());
  if (filters.productCategoryId) params.set('productCategoryId', filters.productCategoryId);
  if (filters.tagId) params.set('tagId', filters.tagId);
  if (filters.onlyActive) params.set('onlyActive', 'true');
  if (filters.lowStockOnly) params.set('lowStockOnly', 'true');
  const query = params.toString();
  return query ? `?${query}` : '';
}

export function useProducts(filters: ProductFilters) {
  const [data, setData] = useState<ProductListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const query = toQueryString(filters);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.get<ProductListResponse>(`/products${query}`));
      setError(null);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not load products.');
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, loading, error, reload: load };
}

export function useProductCategories() {
  const [categories, setCategories] = useState<ProductCategoryDto[]>([]);

  const load = useCallback(async () => {
    try {
      setCategories(await api.get<ProductCategoryDto[]>('/product-categories'));
    } catch {
      setCategories([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { categories, reload: load };
}

export function useTags() {
  const [tags, setTags] = useState<TagDto[]>([]);

  const load = useCallback(async () => {
    try {
      setTags(await api.get<TagDto[]>('/product-tags'));
    } catch {
      setTags([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { tags, reload: load };
}

export async function submitOrder(payload: CreateOrderInput): Promise<OrderDto> {
  const order = await api.post<OrderDto>('/orders', payload);
  void sendDrawnTickets(order);
  return order;
}

/**
 * Draw each station ticket and offer it to the job already queued for it.
 *
 * FIRE AND FORGET, AND SILENT ON FAILURE, for the same reasons as the receipt:
 * the order is placed, the jobs already carry their typed bodies, and every way
 * this can fail ends with the kitchen getting the typed ticket it would have
 * had anyway. Telling a cashier that a drawing failed would be reporting a
 * problem they cannot act on and that costs them nothing.
 *
 * A PORTAL order never reaches here at all — there is no browser on the far end
 * of one — so those keep printing typed, which is why the server still queues a
 * text body for every ticket.
 */
async function sendDrawnTickets(order: OrderDto): Promise<void> {
  for (const ticket of order.tickets) {
    if (!ticket.printJobId) continue;
    try {
      const raster = stationTicketToRaster(ticket);
      if (!raster) continue;
      await uploadRaster(ticket.printJobId, raster);
    } catch {
      // See above: the typed ticket prints regardless.
    }
  }
}
